/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** One entry in a grid's Active Search Criteria box: the column header and what it is filtered to. */
export interface ActiveFilter {
    key: string;
    value: string[];
}

/** The parts of one column's ag-Grid filter model that say what it is filtered to. */
export interface ColumnFilterModel {
    values?: unknown[] | null;
    filterType?: string;
    dateFrom?: string | null;
    filter?: unknown;
}

/**
 * Reads an ag-Grid filter model as Active Search Criteria entries. A dropdown filter carries `values`; a text filter
 * carries a single `filter` and no `values`, so both shapes are read. Filters with nothing chosen are left out.
 *
 * @param headerOf the header shown for a column id; the id itself is used when it has none
 */
export function describeActiveFilters(filterModel: Record<string, ColumnFilterModel> | null | undefined,
                                      headerOf: (colId: string) => string | undefined): ActiveFilter[] {
    const filters: ActiveFilter[] = [];
    for (const [colId, model] of Object.entries(filterModel ?? {})) {
        const value = filterModelValues(model);
        if (value.length > 0) {
            filters.push({key: headerOf(colId) || colId, value});
        }
    }
    return filters;
}

/**
 * What one column's filter model is filtered to: a dropdown's chosen `values`, a text filter's typed `filter`, or the
 * day (`yyyy-mm-dd`) of a date filter, whose `dateFrom` also carries a time.
 */
export function filterModelValues(model: ColumnFilterModel | null | undefined): string[] {
    if (Array.isArray(model?.values)) {
        return model.values.map(String);
    }
    if (model?.filterType === 'date') {
        return model.dateFrom ? [String(model.dateFrom).slice(0, 10)] : [];
    }
    return (model?.filter != null && model.filter !== '') ? [String(model.filter)] : [];
}
